import React, { useMemo, memo, createContext, useContext, useState, useRef, useCallback, CSSProperties } from 'react';
import { useRecoilValue } from 'recoil';
import { cn } from '@/lib/utils';
import {
  componentPositionsState,
  DropZone,
  ComponentType,
  LayoutPositions
} from '@/state/dragDrop';
import { componentVisibilityState, ComponentVisibility } from '@/state/layout';
import { layoutPresetState, LayoutPreset } from '@/state/layoutPreset';
import { DraggableComponent } from './DraggableComponent';
import { DropZoneProvider } from './DropZoneProvider';
import GraphTabSelector from './GraphTabSelector';
import StixDownloadButton from '@/components/StixDownloadButton';
import Graph from '@/components/Graph';
import { TerminalSlot } from '@/components/terminalHost';
import DebugInfoButton from '@/components/chat/DebugInfoButton';
import ThreadNotesButton from '@/components/chat/ThreadNotesButton';
import ThreadTokenUsage from '@/components/chat/ThreadTokenUsage';

const LEFT_ZONES: DropZone[] = ['top-left', 'left', 'bottom-left'];
const ZONE_VERTICAL_ORDER: Record<DropZone, number> = {
  'top-left': 0, 'left': 1, 'bottom-left': 2,
  'top-right': 0, 'right': 1, 'bottom-right': 2
};

const MIN_SPLIT = 15;
const MAX_SPLIT = 85;

interface DragDropLayoutProps {
  children: React.ReactNode;
  className?: string;
}

const ChatContext = createContext<React.ReactNode>(null);

const ChatWrapper = memo(() => {
  const chatContent = useContext(ChatContext);
  return (
    <div className="flex flex-col h-full bg-card shadow-sm border border-border rounded-lg overflow-hidden min-w-0">
      {chatContent}
    </div>
  );
});
ChatWrapper.displayName = 'ChatWrapper';

/**
 * Builds a CSS Grid layout from a curated preset. Bypasses the position-driven
 * drag-and-drop layout so the user can switch to a pre-designed workspace.
 */
function computePresetLayout(
  preset: Exclude<LayoutPreset, 'classic'>,
  visibility: ComponentVisibility
): GridLayout {
  const areas: Record<ComponentType, string | undefined> = {
    chat: visibility.chat ? 'chat' : undefined,
    graph: visibility.graph ? 'graph' : undefined,
    terminal: visibility.terminal ? 'terminal' : undefined
  };
  const hasChat = visibility.chat;
  const hasGraph = visibility.graph;
  const hasTerminal = visibility.terminal;
  const gap = '8px';

  if (preset === 'cockpit') {
    if (hasChat && hasGraph && hasTerminal) {
      return {
        container: {
          display: 'grid',
          gridTemplateAreas: '"chat graph" "chat terminal"',
          gridTemplateColumns: '3fr 2fr',
          gridTemplateRows: '1fr 1fr',
          gap
        },
        areas
      };
    }
    if (hasChat && hasGraph) {
      return {
        container: {
          display: 'grid',
          gridTemplateAreas: '"chat graph"',
          gridTemplateColumns: '3fr 2fr',
          gridTemplateRows: '1fr',
          gap
        },
        areas
      };
    }
    if (hasChat && hasTerminal) {
      return {
        container: {
          display: 'grid',
          gridTemplateAreas: '"chat terminal"',
          gridTemplateColumns: '3fr 2fr',
          gridTemplateRows: '1fr',
          gap
        },
        areas
      };
    }
  }

  if (preset === 'focus') {
    if (hasChat && (hasGraph || hasTerminal)) {
      const rightArea = hasGraph && hasTerminal
        ? '"chat graph" "chat terminal"'
        : hasGraph
          ? '"chat graph"'
          : '"chat terminal"';
      const rows = hasGraph && hasTerminal ? '1fr 1fr' : '1fr';
      return {
        container: {
          display: 'grid',
          gridTemplateAreas: rightArea,
          gridTemplateColumns: '7fr 3fr',
          gridTemplateRows: rows,
          gap
        },
        areas
      };
    }
  }

  if (preset === 'split') {
    if (hasChat && hasGraph && hasTerminal) {
      return {
        container: {
          display: 'grid',
          gridTemplateAreas: '"chat chat" "graph terminal"',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '3fr 2fr',
          gap
        },
        areas
      };
    }
    if (hasGraph && hasTerminal && !hasChat) {
      return {
        container: {
          display: 'grid',
          gridTemplateAreas: '"graph terminal"',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr',
          gap
        },
        areas
      };
    }
    if (hasChat && (hasGraph || hasTerminal)) {
      const bottom = hasGraph ? 'graph' : 'terminal';
      return {
        container: {
          display: 'grid',
          gridTemplateAreas: `"chat" "${bottom}"`,
          gridTemplateColumns: '1fr',
          gridTemplateRows: '3fr 2fr',
          gap
        },
        areas
      };
    }
  }

  const singles: ComponentType[] = [];
  if (hasChat) singles.push('chat');
  if (hasGraph) singles.push('graph');
  if (hasTerminal) singles.push('terminal');
  if (singles.length === 0) {
    return {
      container: { display: 'grid', gridTemplateColumns: '1fr', gridTemplateRows: '1fr' },
      areas
    };
  }
  return {
    container: {
      display: 'grid',
      gridTemplateAreas: singles.map((c) => `"${c}"`).join(' '),
      gridTemplateColumns: '1fr',
      gridTemplateRows: singles.map(() => '1fr').join(' '),
      gap
    },
    areas
  };
}

const COMPONENTS: ComponentType[] = ['chat', 'graph', 'terminal'];

interface ResizeHandleProps {
  direction: 'horizontal' | 'vertical';
  containerRef: React.RefObject<HTMLDivElement | null>;
  onResize: (value: number) => void;
  style: CSSProperties;
}

const ResizeHandle = memo<ResizeHandleProps>(({ direction, containerRef, onResize, style }) => {
  const isVertical = direction === 'vertical';
  const [active, setActive] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setActive(true);
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const size = isVertical ? rect.width : rect.height;
    const origin = isVertical ? rect.left : rect.top;

    const onMove = (me: MouseEvent) => {
      const pos = isVertical ? me.clientX : me.clientY;
      const pct = ((pos - origin) / size) * 100;
      onResize(Math.min(Math.max(pct, MIN_SPLIT), MAX_SPLIT));
    };

    const onUp = () => {
      setActive(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = isVertical ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [containerRef, isVertical, onResize]);

  return (
    <div
      onMouseDown={handleMouseDown}
      className={cn(
        "absolute z-40 group",
        isVertical ? "cursor-col-resize" : "cursor-row-resize"
      )}
      style={{
        ...style,
        ...(isVertical
          ? { width: '12px', transform: 'translateX(-50%)' }
          : { height: '12px', transform: 'translateY(-50%)' })
      }}
    >
      <div
        className={cn(
          "rounded-full transition-colors duration-150",
          isVertical ? "w-[3px] h-full mx-auto" : "h-[3px] w-full my-auto",
          active
            ? "bg-primary"
            : "bg-transparent group-hover:bg-slate-400 dark:group-hover:bg-slate-500"
        )}
      />
    </div>
  );
});
ResizeHandle.displayName = 'ResizeHandle';

interface IntersectionHandleProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  onResizeCol: (value: number) => void;
  onResizeRow: (value: number) => void;
  style: CSSProperties;
}

const IntersectionHandle = memo<IntersectionHandleProps>(({ containerRef, onResizeCol, onResizeRow, style }) => {
  const [active, setActive] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setActive(true);
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const onMove = (me: MouseEvent) => {
      const xPct = ((me.clientX - rect.left) / rect.width) * 100;
      const yPct = ((me.clientY - rect.top) / rect.height) * 100;
      onResizeCol(Math.min(Math.max(xPct, MIN_SPLIT), MAX_SPLIT));
      onResizeRow(Math.min(Math.max(yPct, MIN_SPLIT), MAX_SPLIT));
    };

    const onUp = () => {
      setActive(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'move';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [containerRef, onResizeCol, onResizeRow]);

  return (
    <div
      onMouseDown={handleMouseDown}
      className="absolute z-50 cursor-move group"
      style={{
        ...style,
        width: '16px',
        height: '16px',
        transform: 'translate(-50%, -50%)'
      }}
    >
      <div
        className={cn(
          "w-3 h-3 m-auto rounded-sm transition-colors duration-150",
          active
            ? "bg-primary"
            : "bg-transparent group-hover:bg-slate-400 dark:group-hover:bg-slate-500"
        )}
      />
    </div>
  );
});
IntersectionHandle.displayName = 'IntersectionHandle';

interface GridLayout {
  container: CSSProperties;
  areas: Record<ComponentType, string | undefined>;
}

function computeGridLayout(
  positions: LayoutPositions,
  visibility: ComponentVisibility,
  colSplit: number,
  rowSplit: number
): GridLayout {
  const visible = COMPONENTS.filter(c => visibility[c]);
  const areas: Record<ComponentType, string | undefined> = {
    chat: undefined,
    graph: undefined,
    terminal: undefined
  };

  if (visible.length === 0) {
    return {
      container: { display: 'grid', gridTemplateColumns: '1fr', gridTemplateRows: '1fr' },
      areas
    };
  }

  const sortByZone = (a: ComponentType, b: ComponentType) => {
    const zoneOrderDiff = ZONE_VERTICAL_ORDER[positions[a].zone] - ZONE_VERTICAL_ORDER[positions[b].zone];
    if (zoneOrderDiff !== 0) return zoneOrderDiff;
    return positions[a].order - positions[b].order;
  };

  const leftComponents = visible
    .filter(c => LEFT_ZONES.includes(positions[c].zone))
    .sort(sortByZone);
  const rightComponents = visible
    .filter(c => !LEFT_ZONES.includes(positions[c].zone))
    .sort(sortByZone);

  const hasLeft = leftComponents.length > 0;
  const hasRight = rightComponents.length > 0;

  if (hasLeft && hasRight) {
    const maxRows = Math.max(leftComponents.length, rightComponents.length);
    const gridAreas: string[] = [];

    for (let r = 0; r < maxRows; r++) {
      const leftComp = leftComponents[Math.min(r, leftComponents.length - 1)];
      const rightComp = rightComponents[Math.min(r, rightComponents.length - 1)];
      gridAreas.push(`"${leftComp} ${rightComp}"`);
    }

    visible.forEach(c => { areas[c] = c; });

    return {
      container: {
        display: 'grid',
        gridTemplateAreas: gridAreas.join(' '),
        gridTemplateColumns: `${colSplit}fr ${100 - colSplit}fr`,
        gridTemplateRows: maxRows > 1
          ? `${rowSplit}fr ${100 - rowSplit}fr`
          : '1fr',
        gap: '4px'
      },
      areas
    };
  }

  const components = hasLeft ? leftComponents : rightComponents;
  const gridAreas = components.map(c => `"${c}"`).join(' ');
  components.forEach(c => { areas[c] = c; });

  return {
    container: {
      display: 'grid',
      gridTemplateAreas: gridAreas,
      gridTemplateColumns: '1fr',
      gridTemplateRows: components.length === 2
        ? `${rowSplit}fr ${100 - rowSplit}fr`
        : components.map(() => '1fr').join(' '),
      gap: '4px'
    },
    areas
  };
}

/**
 * Stable drag-and-drop layout using CSS Grid with resize handles.
 * All three components (chat, graph, terminal) are always mounted to preserve
 * state (terminal connections, etc.). Visibility is controlled via CSS display.
 */
export const DragDropLayout: React.FC<DragDropLayoutProps> = ({ children, className }) => {
  const positions = useRecoilValue(componentPositionsState);
  const visibility = useRecoilValue(componentVisibilityState);
  const preset = useRecoilValue(layoutPresetState);
  const containerRef = useRef<HTMLDivElement>(null);
  const [colSplit, setColSplit] = useState(50);
  const [rowSplit, setRowSplit] = useState(50);

  const gridLayout = useMemo(
    () =>
      preset === 'classic'
        ? computeGridLayout(positions, visibility, colSplit, rowSplit)
        : computePresetLayout(preset, visibility),
    [preset, positions, visibility, colSplit, rowSplit]
  );

  const handleConfig = useMemo(() => {
    if (preset !== 'classic') {
      return { showCol: false, showRow: false, rowSide: 'full' as const };
    }
    const visible = COMPONENTS.filter(c => visibility[c]);
    const left = visible.filter(c => LEFT_ZONES.includes(positions[c].zone));
    const right = visible.filter(c => !LEFT_ZONES.includes(positions[c].zone));

    const showCol = left.length > 0 && right.length > 0;
    const showRow = (showCol && (left.length === 2 || right.length === 2))
      || (!showCol && visible.length === 2);
    const rowSide: 'left' | 'right' | 'full' = !showCol
      ? 'full'
      : left.length > 1
        ? 'left'
        : 'right';

    return { showCol, showRow, rowSide };
  }, [preset, positions, visibility]);

  const visibleCount = Object.values(visibility).filter(Boolean).length;

  return (
    <ChatContext.Provider value={children}>
      <DropZoneProvider>
        <div
          ref={containerRef}
          className={cn(
            'h-full w-full relative',
            preset !== 'classic' && 'p-2 bg-background',
            className
          )}
          style={gridLayout.container}
        >
          {visibleCount === 0 && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-slate-600 dark:text-slate-400">
                <p className="text-lg font-medium">No components visible</p>
                <p className="text-sm">Use the layout controls in the header to show components</p>
              </div>
            </div>
          )}

          <div
            style={{
              gridArea: gridLayout.areas.chat,
              display: visibility.chat ? undefined : 'none'
            }}
            className="min-h-0 min-w-0 overflow-hidden h-full"
          >
            <DraggableComponent
              componentType="chat"
              sourceZone={positions.chat.zone}
              tabExtras={
                <>
                  <ThreadTokenUsage />
                  <ThreadNotesButton />
                  <DebugInfoButton />
                </>
              }
            >
              <ChatWrapper />
            </DraggableComponent>
          </div>

          <div
            style={{
              gridArea: gridLayout.areas.graph,
              display: visibility.graph ? undefined : 'none'
            }}
            className="min-h-0 min-w-0 overflow-hidden h-full"
          >
            <DraggableComponent
              componentType="graph"
              sourceZone={positions.graph.zone}
              tabExtras={
                <>
                  <GraphTabSelector />
                  <StixDownloadButton />
                </>
              }
            >
              <Graph className="h-full w-full" />
            </DraggableComponent>
          </div>

          <div
            style={{
              gridArea: gridLayout.areas.terminal,
              display: visibility.terminal ? undefined : 'none'
            }}
            className="min-h-0 min-w-0 overflow-hidden h-full"
          >
            <DraggableComponent componentType="terminal" sourceZone={positions.terminal.zone}>
              <TerminalSlot className="h-full w-full" />
            </DraggableComponent>
          </div>

          {handleConfig.showCol && (
            <ResizeHandle
              direction="vertical"
              containerRef={containerRef}
              onResize={setColSplit}
              style={{ left: `${colSplit}%`, top: 0, bottom: 0 }}
            />
          )}

          {handleConfig.showRow && (
            <ResizeHandle
              direction="horizontal"
              containerRef={containerRef}
              onResize={setRowSplit}
              style={{
                top: `${rowSplit}%`,
                ...(handleConfig.rowSide === 'full' && { left: 0, right: 0 }),
                ...(handleConfig.rowSide === 'left' && { left: 0, width: `${colSplit}%` }),
                ...(handleConfig.rowSide === 'right' && { left: `${colSplit}%`, width: `${100 - colSplit}%` }),
              }}
            />
          )}

          {handleConfig.showCol && handleConfig.showRow && (
            <IntersectionHandle
              containerRef={containerRef}
              onResizeCol={setColSplit}
              onResizeRow={setRowSplit}
              style={{ left: `${colSplit}%`, top: `${rowSplit}%` }}
            />
          )}
        </div>
      </DropZoneProvider>
    </ChatContext.Provider>
  );
};
